<template>
  <div class="model-selector">
    <!--START: Selected Pill-->
    <button
      type="button"
      class="model-pill"
      :class="{ 'is-open': showMenu, 'is-disabled': isDisabled }"
      :disabled="isDisabled"
      @click="toggleMenu"
    >
      <OpenAiLogo class="model-logo" />
      <span class="model-label">{{ selectedLabel }}</span>
      <unicon class="model-chevron" name="angle-down"></unicon>
    </button>
    <!--END: Selected Pill-->

    <!--START: Dropdown Menu-->
    <template v-if="showMenu">
      <div class="model-menu-backdrop" @click="closeMenu"></div>
      <div class="model-menu">
        <button
          v-for="item in options"
          :key="item.value"
          type="button"
          class="model-option"
          :class="{ 'is-selected': item.value === selectedValue }"
          @click="selectModel(item)"
        >
          <OpenAiLogo class="model-logo" />
          <span class="model-option-text">
            <span class="model-option-name">{{ item.name }}</span>
            <span v-if="item.description" class="model-option-desc">{{
              item.description
            }}</span>
          </span>
          <unicon
            v-if="item.value === selectedValue"
            class="model-check"
            name="check"
          ></unicon>
        </button>
      </div>
    </template>
    <!--END: Dropdown Menu-->
  </div>
</template>

<script>
// OpenAI logomark rendered as an inline SVG via a render function so it works
// with the runtime-only Vue build (no string templates).
const OpenAiLogo = {
  name: "OpenAiLogo",
  render(h) {
    return h(
      "svg",
      {
        attrs: { viewBox: "0 0 24 24", xmlns: "http://www.w3.org/2000/svg" },
      },
      [
        h("path", {
          attrs: {
            d: "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z",
          },
        }),
      ]
    );
  },
};

export default {
  name: "ModelSelector",
  components: { OpenAiLogo },
  props: {
    field: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showMenu: false,
    };
  },
  computed: {
    // Available models — { name, value, description } shape
    options() {
      return this.field.fields || [];
    },
    isDisabled() {
      return this.field.disabled === true;
    },
    // The field value follows the dropdown contract: [displayName, modelValue]
    selectedValue() {
      return Array.isArray(this.field.value) && this.field.value.length
        ? this.field.value[1]
        : "";
    },
    selectedLabel() {
      if (Array.isArray(this.field.value) && this.field.value.length) {
        return this.field.value[0];
      }
      return this.field.placeholder || "Select model";
    },
  },
  methods: {
    toggleMenu() {
      if (this.isDisabled) return;
      this.showMenu = !this.showMenu;
    },
    closeMenu() {
      this.showMenu = false;
    },
    selectModel(item) {
      this.field.value = [item.name, item.value];
      this.showMenu = false;
      this.$emit("fieldChanged");
    },
  },
};
</script>

<style scoped lang="scss">
/* Hug the pill so the row's flex:1 title keeps its width and the pill is
   pushed flush to the right edge. The !important overrides the framework's
   .field-wrapper min/max-width, which otherwise squeezes the title. */
.model-selector {
  position: relative;
  display: inline-flex;
  min-width: 0 !important;
  max-width: none !important;
}

/* The selected-state pill */
.model-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: $whiteColor;
  border: 1px solid $inputBorderColor;
  border-radius: 0.7rem;
  padding: 0.5rem 0.8rem;
  cursor: pointer;
  outline: none;
  font-size: $smallFontSize;
  font-weight: $mediumFontWeight;
  color: $darkBlackColor;
  transition: all $transitionSpeed;

  &:hover {
    border-color: darken($inputBorderColor, 8%);
  }

  &.is-open {
    border-color: darken($inputBorderColor, 12%);
  }

  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.model-logo {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  fill: $darkBlackColor;
}

.model-label {
  white-space: nowrap;
}

.model-chevron {
  display: inline-flex;
  margin-left: 0.15rem;
  transition: transform $transitionSpeed;

  /deep/ svg {
    width: 1rem;
    height: 1rem;
    fill: lighten($greyColor, 25%);
  }
}

.model-pill.is-open .model-chevron {
  transform: rotate(180deg);
}

/* Invisible click-catcher so clicking anywhere closes the menu */
.model-menu-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 49;
}

/* Opens upward and anchored right so it stays within the card and isn't
   clipped at the bottom of the page */
.model-menu {
  position: absolute;
  bottom: calc(100% + 0.45rem);
  top: auto;
  right: 0;
  left: auto;
  z-index: 50;
  width: 18rem;
  background-color: $whiteColor;
  border: 1px solid $inputBorderColor;
  border-radius: 0.8rem;
  box-shadow: 0 0.75rem 1.75rem rgba(27, 27, 27, 0.14);
  padding: 0.4rem;
}

.model-option {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.6rem 0.65rem;
  border: none;
  background: transparent;
  border-radius: 0.55rem;
  cursor: pointer;
  outline: none;
  text-align: left;
  transition: background-color $transitionSpeed;

  & + .model-option {
    margin-top: 0.15rem;
  }

  &:hover {
    background-color: darken($whiteColor, 4%);
  }

  &.is-selected {
    background-color: rgba(78, 69, 221, 0.07);
  }
}

.model-option .model-logo {
  margin-top: 0.1rem;
}

.model-option-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.model-option-name {
  font-size: 0.92rem;
  font-weight: $mediumFontWeight;
  color: $darkBlackColor;
  line-height: 1.3;
}

.model-option-desc {
  margin-top: 0.1rem;
  font-size: 0.78rem;
  font-weight: 400;
  color: lighten($greyColor, 22%);
  line-height: 1.3;
}

.model-check {
  display: inline-flex;
  flex-shrink: 0;
  align-self: center;

  /deep/ svg {
    width: 1rem;
    height: 1rem;
    fill: $secondaryColor;
  }
}
</style>
